/* ==========================================================================
   Accessibility behavior
   --------------------------------------------------------------------------
   Custom JS added to address accessibility findings without editing the
   theme's compiled bundle (theme.aio.min.js) or third-party app scripts
   directly (Globo filter, Kaktus wishlist, etc).

   Loaded after theme.aio.min.js in layout/theme.liquid, deferred.
   ========================================================================== */

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    initPrimaryNavDropdowns();
    initWishlistButtons();
    initCartAnnouncer();
    initBackgroundVideoPause();
    initGloboFilterA11y();
    initFlickityDotsA11y();
    initMegamenuColumnToggles();
    initAddToCartRetarget();
    initGalleryThumbFocusFix();
    initProductVideoControls();
    initOutboundLinkHints();
    initIframeTitles();
  });

  /* ------------------------------------------------------------------------
     Product gallery videos: controls on hover/focus only (WCAG 2.1.1).

     media.liquid now renders these with `controls` and tabindex="0" - before,
     muted gallery videos had neither, so there was no way to play one by
     keyboard at all. But these videos are small and the native control bar
     covers a noticeable share of the frame, so it is hidden until the video is
     hovered or focused.

     Done by toggling the attribute rather than styling the bar away:
     ::-webkit-media-controls is non-standard and does nothing in Firefox,
     which would leave the bar permanently visible there.

     The markup ships WITH controls and this removes them, not the reverse.
     That way a JS failure leaves the video fully operable with a visible bar,
     rather than unusable - the accessible state is the default, not the
     enhancement.

     Controls stay while the video is playing, so a user who has started one
     can always reach pause without hunting for it.
     ------------------------------------------------------------------------ */
  function initProductVideoControls() {
    var videos = document.querySelectorAll('video.product__video');
    if (!videos.length) return;

    videos.forEach(function (video) {
      function show() {
        video.setAttribute('controls', '');
      }
      function hide() {
        if (video.paused && !video.matches(':hover') && document.activeElement !== video) {
          video.removeAttribute('controls');
        }
      }

      hide();
      video.addEventListener('mouseenter', show);
      video.addEventListener('focus', show);
      video.addEventListener('play', show);
      video.addEventListener('mouseleave', hide);
      video.addEventListener('blur', hide);
      video.addEventListener('pause', hide);
    });

    // The main viewer sits BEFORE the thumbnails in the DOM, so the video's own
    // tab stop is already behind you by the time you tab to its thumbnail -
    // reach the video thumb, press Tab, and you leave the gallery entirely. You
    // would have to know to Shift+Tab backwards to get at the controls, which
    // nobody would guess.
    //
    // So activating the video's thumbnail moves focus to the video itself.
    // That matches what the thumbnail already does visually (it swaps the main
    // viewer to that media), and it is the behaviour a media gallery should
    // have: choose the video, land on the video.
    //
    // Focus moves on ACTIVATION only, never on focus alone - the theme swaps
    // slides as you merely tab across thumbnails, and hijacking focus during
    // that would make browsing the gallery impossible.
    videos.forEach(function (video) {
      var wrapper = video.closest('[data-index]');
      var index = wrapper && wrapper.getAttribute('data-index');
      if (index === null || index === undefined) return;

      var thumb = document.querySelector('[data-product-thumb][data-index="' + index + '"]');
      if (thumb) {
        // Without this the thumb announces as the poster image's alt text,
        // giving no clue that choosing it leads to a video.
        thumb.setAttribute('aria-label', video.getAttribute('aria-label') || 'Video');
      }

      document.addEventListener('click', function (evt) {
        var hit = evt.target.closest && evt.target.closest('[data-product-thumb]');
        if (!hit || hit.getAttribute('data-index') !== index) return;
        // Deliberately not preventDefault - the theme's own handler switches
        // the slide. Focus after it has done so.
        setTimeout(function () {
          video.focus();
        }, 50);
      });
    });
  }

  /* ------------------------------------------------------------------------
     target="_blank" links: announce that they open a new window (WCAG 3.2.5).

     target="_blank" is invisible to assistive tech - no screen reader
     announces it - so a blind visitor is moved to a new tab silently and the
     Back button then does nothing, which is disorienting. The warning has to
     be part of the link's accessible name.

     Applied here rather than left to whoever writes the content: page content
     on this store is edited by several people across both Shopify's editor and
     PageFly, and a convention that depends on everyone remembering it will not
     hold. This covers every existing outbound link and anything added later.

     Skips links that already say it (the theme's own social icons include the
     phrase already), and links whose accessible name is otherwise empty - an
     unnamed link announcing only "(opens in a new window)" is worse than
     leaving it for the separate fix it needs.
     ------------------------------------------------------------------------ */
  function initOutboundLinkHints() {
    var phrase = (window.theme && theme.strings && theme.strings.opensInNewWindow) || '(opens in a new window)';
    var needle = phrase.replace(/[()]/g, '').trim().toLowerCase();

    function currentName(link) {
      return (link.getAttribute('aria-label') || link.textContent || '').trim();
    }

    function decorate() {
      document.querySelectorAll('a[target="_blank"]:not([data-a11y-newwin])').forEach(function (link) {
        link.setAttribute('data-a11y-newwin', '');

        var name = currentName(link);
        if (!name) return;                                   // unnamed link - a different problem
        if (name.toLowerCase().indexOf(needle) !== -1) return; // already says it

        if (link.hasAttribute('aria-label')) {
          // aria-label wins over content, so appending a span would be ignored.
          link.setAttribute('aria-label', link.getAttribute('aria-label') + ' ' + phrase);
          return;
        }
        var hint = document.createElement('span');
        hint.className = 'visually-hidden';
        hint.textContent = ' ' + phrase;
        link.appendChild(hint);
      });
    }

    decorate();
    watchForNewNodes(decorate);
  }

  /* ------------------------------------------------------------------------
     Untitled <iframe>s (WCAG 4.1.2 / 2.4.1).

     An iframe with no title announces as just "frame" - embedded Google Forms,
     maps and videos give no clue what they are. Titles are derived from the
     src where the host is recognisable, since "Google Form" is far more useful
     than a generic label.

     Hidden iframes are left alone deliberately: apps inject plenty of 0x0
     tracking frames, and display:none already keeps them out of the
     accessibility tree. Titling them would be noise at best.
     ------------------------------------------------------------------------ */
  function initIframeTitles() {
    var generic = (window.theme && theme.strings && theme.strings.embeddedContent) || 'Embedded content';
    var hosts = [
      { match: /docs\.google\.com\/forms/i, title: 'Google Form' },
      { match: /google\.com\/maps|maps\.google\./i, title: 'Google Map' },
      { match: /youtube(-nocookie)?\.com|youtu\.be/i,  title: 'YouTube video' },
      { match: /player\.vimeo\.com|vimeo\.com/i,      title: 'Vimeo video' },
      { match: /open\.spotify\.com/i,                  title: 'Spotify player' },
      { match: /instagram\.com/i,                       title: 'Instagram post' },
      { match: /facebook\.com/i,                        title: 'Facebook post' }
    ];

    function decorate() {
      document.querySelectorAll('iframe:not([title]):not([data-a11y-frame])').forEach(function (frame) {
        frame.setAttribute('data-a11y-frame', '');
        if (frame.getAttribute('aria-hidden') === 'true') return;
        // Tracking pixels and the like - no size, so nothing to describe.
        if (!frame.offsetWidth && !frame.offsetHeight) return;

        var src = frame.getAttribute('src') || frame.getAttribute('data-src') || '';
        var title = generic;
        for (var i = 0; i < hosts.length; i++) {
          if (hosts[i].match.test(src)) { title = hosts[i].title; break; }
        }
        frame.setAttribute('title', title);
      });
    }

    decorate();
    watchForNewNodes(decorate);
  }

  /* Shared, throttled observer helper. Only runs the callback when nodes were
     actually added - attribute-only mutations are frequent and irrelevant
     here, and an unthrottled observer on this store has previously been heavy
     enough to stall page paints. */
  function watchForNewNodes(callback) {
    var pending = false;
    var observer = new MutationObserver(function (records) {
      var added = false;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) { added = true; break; }
      }
      if (!added || pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        callback();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Flickity slide-nav dots (Press/Centennial hero sliders, and any other
     slideshow with dots enabled). Confirmed against the actual bundled
     Flickity source: dots are plain <li>s with a click handler only - no
     tabindex, no role. Flickity creates them dynamically after init, so we
     decorate via MutationObserver rather than a fixed Liquid anchor.
     ------------------------------------------------------------------------ */
  function initFlickityDotsA11y() {
    var pending = false;

    function decorate() {
      document.querySelectorAll('.flickity-page-dots').forEach(function (list) {
        var dots = list.querySelectorAll('li.dot');
        dots.forEach(function (dot, i) {
          if (!dot.hasAttribute('data-a11y-dot-bound')) {
            dot.setAttribute('data-a11y-dot-bound', 'true');
            dot.setAttribute('role', 'button');
            dot.setAttribute('tabindex', '0');
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            dot.addEventListener('keydown', function (evt) {
              if (evt.key === 'Enter' || evt.key === ' ' || evt.keyCode === 13 || evt.keyCode === 32) {
                evt.preventDefault();
                dot.click();
              }
            });
          }
          // Flickity toggles is-selected on its own select/cellChange events -
          // syncing aria-current from that class on every decorate() pass
          // keeps it accurate regardless of whether the change came from a
          // dot click, an arrow, or autoplay advancing on its own.
          dot.setAttribute('aria-current', dot.classList.contains('is-selected') ? 'true' : 'false');
        });
      });
    }

    decorate();

    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        decorate();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Globo filter panel: "Skip to products" link + price slider focus
     restore. Globo injects its whole filter UI via its own JS - it's not
     server-rendered, so there's no Liquid anchor to hook. We wait for it to
     actually appear via MutationObserver rather than assuming a fixed
     load time.
     ------------------------------------------------------------------------ */
  function initGloboFilterA11y() {

    // Focus outline via a CSS rule (.noUi-handle:focus-visible in
    // accessibility.css) wasn't showing - Globo's own CSS is app-hosted, so
    // it's likely injected via JS at runtime, meaning it can load AFTER our
    // stylesheet and win a specificity tie on cascade order alone, or set
    // outline via inline style directly. Setting the outline here as an
    // inline style with 'important' priority - the strongest the CSS
    // cascade allows short of a user stylesheet - sidesteps that fight
    // entirely rather than continuing to guess at Globo's exact CSS.
    document.addEventListener('focusin', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        handle.style.setProperty('outline', '3px solid #005fcc', 'important');
        handle.style.setProperty('outline-offset', '2px', 'important');
      }
    });
    document.addEventListener('focusout', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        handle.style.removeProperty('outline');
        handle.style.removeProperty('outline-offset');
      }
    });

    // A real <button> with a DELEGATED handler, not an <a href="#...">.
    //
    // The anchor version failed repeatedly for two compounding reasons:
    //   1. A fragment jump scrolls but does not move focus when the target is
    //      stamped after page load - so the next Tab carried on from the link
    //      straight back into the filters, the exact bug this exists to fix.
    //   2. Globo re-renders its filter panel by re-inserting markup, so the
    //      link came back as a copy WITHOUT its event listeners. It looked
    //      present and its href still scrolled, but nothing else ran.
    //
    // A button has no default navigation to fight, and one listener on
    // document survives any amount of re-rendering or cloning underneath it.
    // The target is resolved at click time rather than remembered, so it does
    // not matter how many times Globo has replaced the grid.
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('.a11y-skip-btn');
      if (!btn) return;
      evt.preventDefault();
      var firstProduct = document.querySelector('#ProductGrid .grid-product');
      var target = firstProduct && firstProduct.parentNode;
      if (!target) return;
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.scrollIntoView({ block: 'start' });
    });

    // Presence check rather than a one-shot flag: Globo removes the button
    // along with its own panel on every re-render, and a latch would mean it
    // never came back after the first filter change.
    function ensureSkipButton() {
      if (document.querySelector('.a11y-skip-btn')) return;
      var filterEl = document.querySelector('.gf-filter-header, .gf-filter-trigger');
      if (!filterEl || !filterEl.parentNode) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-skip-btn visually-hidden skip-link';
      btn.textContent = (window.theme && theme.strings && theme.strings.skipToProducts) || 'Skip to products';
      filterEl.parentNode.insertBefore(btn, filterEl);
    }

    // Restores focus to a price-slider handle Globo just re-rendered (it
    // replaces the slider's markup on value change - e.g. via arrow keys -
    // dropping focus to <body>, confirmed live: an earlier version of this
    // fix used a fixed setTimeout after focusout, assuming the replacement
    // happened almost immediately. It doesn't - Globo's value-change re-
    // render involves an actual AJAX round-trip to refetch filtered
    // results, which reliably takes longer than any short fixed delay, so
    // the check fired before the real replacement and found nothing wrong.
    // Reacting to the actual DOM mutation (via the same observer that
    // handles the skip link, whenever it actually fires) instead of
    // guessing a delay works regardless of how long that round-trip takes.
    // Tracks which logical handle (lower/upper, via its own data-handle
    // attr) and which slider (via its own id) was focused, so we can find
    // the same one again by value rather than by a node reference that's
    // already gone.
    var lastHandleType = null;
    var lastSliderId = null;

    document.addEventListener('focusin', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        var slider = handle.closest('[id^="slider-"]');
        lastHandleType = handle.getAttribute('data-handle');
        lastSliderId = slider ? slider.id : null;
      } else if (evt.target !== document.body) {
        // Focus moved somewhere else on purpose - stop trying to restore.
        // document.body itself is excluded because that's the transient
        // state right after Globo removes the focused handle, before this
        // same observer has had a chance to restore it - clearing here
        // would abandon the restore attempt before it can run.
        lastHandleType = null;
        lastSliderId = null;
      }
    });

    function restoreSliderFocus() {
      if (!lastSliderId || document.activeElement !== document.body) return;
      var slider = document.getElementById(lastSliderId);
      var handleEl = slider && slider.querySelector('.noUi-handle[data-handle="' + lastHandleType + '"]');
      if (handleEl) handleEl.focus();
    }

    var observer = new MutationObserver(function () {
      ensureSkipButton();
      restoreSliderFocus();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Background/hero video pause control (WCAG 2.2.2).
     video-div.liquid renders <video loop muted playsinline autoplay> with
     no controls at all - no way to pause. Verified this section only ever
     auto-plays once (theme.initWhenVisible, one-shot), never auto-pauses on
     scroll, so a simple toggle button here won't fight any existing logic.
     Liquid provides data-a11y-label (the section's own title/heading text,
     or a generic fallback) on each video tag - we build the button and its
     accessible name from that here rather than duplicating button markup
     across every section that can render one of these videos.
     ------------------------------------------------------------------------ */
  function initBackgroundVideoPause() {
    var videos = document.querySelectorAll('video.video-div[data-type="mp4"]');
    if (!videos.length) return;

    var pauseIcon = '<svg aria-hidden="true" focusable="false" viewBox="0 0 10 13"><path fill="currentColor" d="M0 0h3v13H0zM7 0h3v13H7z"/></svg>';
    var playIcon = '<svg aria-hidden="true" focusable="false" viewBox="18.24 17.35 24.52 28.3"><path fill="currentColor" d="M22.1 19.151v25.5l20.4-13.489-20.4-12.011z"/></svg>';

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    videos.forEach(function (video) {
      var label = video.getAttribute('data-a11y-label') || 'Background video';
      video.setAttribute('aria-label', label);
      // Hints it's clickable, since there's no visible button for
      // mouse/touch users - they toggle via the video itself.
      video.style.cursor = 'pointer';

      // Liquid can't know the visitor's reduced-motion preference at render
      // time, so the video still autoplays briefly before this JS runs -
      // pause it immediately and let the control offer to start it instead.
      if (reduceMotion) {
        video.pause();
      }

      function toggle() {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }

      // Mouse/touch users: click anywhere on the video itself. Clicks on
      // anything stacked visually above it (e.g. a hero CTA link/button)
      // still reach that element instead, per normal hit-testing - no
      // conflict since this listener is on the <video> element itself, not
      // a full-bleed overlay behind it.
      video.addEventListener('click', toggle);

      // Keyboard-only control: visually hidden until it receives focus (see
      // .a11y-video-pause in accessibility.css) - a real, discoverable
      // button for keyboard users without a permanent icon cluttering the
      // hero for everyone else.
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-video-pause';
      // Appended to the section root, NOT next to the <video>. theme.css gives
      // .hero__media a z-index, which makes it a stacking context - a button
      // inside it is trapped below .hero__text-content (z-index 4) and the
      // hero's overlay scrim (z-index 3) no matter how high its own z-index
      // goes. That is why it was visible but unclickable: those layers sit on
      // top and take the pointer events. Hoisting it to the section root lets
      // its z-index actually compete.
      var host = video.closest('.hero, .background-media-text, .video-parent-section') || video.parentNode;
      host.appendChild(btn);
      btn.addEventListener('click', toggle);

      function updateButton() {
        var isPaused = video.paused;
        btn.setAttribute('aria-pressed', isPaused ? 'false' : 'true');
        btn.setAttribute('aria-label', (isPaused ? 'Play ' : 'Pause ') + label);
        btn.innerHTML = isPaused ? playIcon : pauseIcon;
      }

      video.addEventListener('play', updateButton);
      video.addEventListener('pause', updateButton);
      updateButton();
    });
  }

  /* ------------------------------------------------------------------------
     Add-to-cart + quantity screen-reader announcements.
     Adding to cart gives sighted users a small count-bubble update but no
     non-visual confirmation at all, and the +/- quantity buttons change the
     input's value programmatically, which screen readers do not announce.

     This deliberately does NOT hook the theme's ajaxProduct:* events - see
     the note in the body for why they never fire on this store. It watches
     the cart itself instead, which is agnostic to whichever script actually
     performs the add.
     ------------------------------------------------------------------------ */
  function initCartAnnouncer() {
    var statusRegion = document.getElementById('A11yCartStatus');
    var alertRegion = document.getElementById('A11yCartAlert');
    if (!statusRegion || !alertRegion) return;

    function speak(region, message) {
      // Clear first, then set on a short delay - some screen readers won't
      // re-announce a live region if the text content doesn't actually
      // change, which matters if someone adds the same product twice.
      region.textContent = '';
      window.setTimeout(function () {
        region.textContent = message;
      }, 50);
    }

    function cartUrl() {
      return (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
    }

    // IMPORTANT: do NOT rely on the theme's ajaxProduct:added event here.
    // theme.js only instantiates AjaxProduct when cartType === 'drawer':
    //     if (theme.settings.cartType === 'drawer') { new theme.AjaxProduct(...) }
    // This store is cartType 'page', so that event NEVER fires. An earlier
    // version of this function listened for it and was silently dead.
    // Something else on the page (a free-gift / bundle app) intercepts the
    // submit and adds to cart without a page reload, so we cannot depend on
    // any particular script's events. Instead we watch the CART ITSELF:
    // snapshot item_count when the button is pressed, then poll until it
    // changes. That works no matter which script performs the add.

    function cartUrl() {
      return (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
    }

    function getCart() {
      return fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    }

    function productTitleNear(el) {
      var scope = el.closest('[data-section-id]') || document;
      var t = scope.querySelector('.product-single__title, .product__title, h1');
      return t ? t.textContent.trim() : '';
    }

    function quantityFor(btn) {
      var form = btn.closest('form');
      var q = form && form.querySelector('[name="quantity"]');
      // Impulse renders the qty input OUTSIDE the form, linked via form="<id>"
      if (!q && form && form.id) {
        q = document.querySelector('[name="quantity"][form="' + form.id + '"]');
      }
      var n = q ? parseInt(q.value, 10) : 1;
      return (isNaN(n) || n < 1) ? 1 : n;
    }

    function watchCartFor(qty, title) {
      getCart().then(function (before) {
        var start = before.item_count;
        var tries = 0;
        (function poll() {
          if (++tries > 14) return;            // give up after ~7s
          window.setTimeout(function () {
            getCart().then(function (now) {
              if (now.item_count !== start) {
                var count = now.item_count;
                var msg = 'Added ' + qty + ' ' + (title || 'item') +
                          ' to cart. Cart now has ' + count +
                          (count === 1 ? ' item.' : ' items.');
                speak(statusRegion, msg);
              } else {
                poll();
              }
            }).catch(poll);
          }, 500);
        })();
      }).catch(function () {});
    }

    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest &&
                evt.target.closest('[data-add-to-cart], button[name="add"]');
      if (!btn || btn.disabled) return;
      watchCartFor(quantityFor(btn), productTitleNear(btn));
    });

    // Announce the quantity after +/- is pressed. The theme updates the
    // input's value programmatically, and a programmatic value change on a
    // text input is not announced by screen readers, so the user gets no
    // feedback that the number moved.
    document.addEventListener('click', function (evt) {
      var adj = evt.target.closest && evt.target.closest('.js-qty__adjust');
      if (!adj) return;
      var wrap = adj.closest('.js-qty__wrapper');
      var input = wrap && wrap.querySelector('.js-qty__num');
      if (!input) return;
      window.setTimeout(function () {   // let the theme's handler update it first
        speak(statusRegion, 'Quantity: ' + input.value);
      }, 80);
    });
  }

  /* ------------------------------------------------------------------------
     Kaktus Wishlist app buttons are plain <div>s (no role, no tabindex, no
     accessible name) - not focusable, no keyboard path. We can't edit the
     app's own markup and it re-renders these nodes itself on state changes
     (add/remove, count updates), so we decorate on load and re-decorate
     whenever the app mutates the DOM, rather than trying to replace its
     elements with real <button>s (which the app would just overwrite).
     Covers both the PDP "SAVE TO WISHLIST" control and the site-wide
     "MY WISHLIST" viewer trigger - both share the .kaktus-wishlist-button
     class.
     ------------------------------------------------------------------------ */
  function initWishlistButtons() {
    // NOTE: deliberately no "is there a button yet?" early return here. An
    // earlier version bailed out if .kaktus-wishlist-button wasn't already
    // in the DOM at DOMContentLoaded - but Kaktus injects its widget later,
    // from a remote async script, so that guard always fired first and
    // returned before setting up the very observer meant to catch that
    // injection. The observer has to be registered unconditionally.

    function decorate(root) {
      var els = root.querySelectorAll('.kaktus-wishlist-button');
      els.forEach(function (el) {
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');

        var svg = el.querySelector('svg');
        if (svg) svg.setAttribute('aria-hidden', 'true');

        // Toggle state comes from Kaktus's own kaktus-w-completed class on
        // the host element (their stylesheet keys the "saved" background
        // colour off it). Not from the button text: their markup sets
        // data-text and data-text-completed to the SAME string ("Save to
        // Wishlist"), so the label never changes between states.
        el.setAttribute('aria-pressed', el.classList.contains('kaktus-w-completed') ? 'true' : 'false');

        if (!el.hasAttribute('data-a11y-wishlist-bound')) {
          el.setAttribute('data-a11y-wishlist-bound', 'true');
          el.addEventListener('keydown', function (evt) {
            if (evt.key === 'Enter' || evt.key === ' ' || evt.keyCode === 13 || evt.keyCode === 32) {
              evt.preventDefault();
              el.click();
            }
          });
        }
      });
    }

    decorate(document);

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        decorate(document);
      });
    });
    // attributes/attributeFilter matter as much as childList here: Kaktus
    // signals the saved state by toggling the kaktus-w-completed class on
    // the existing element rather than replacing it, which is an attribute
    // mutation - a childList-only observer would leave aria-pressed stale
    // after the user adds or removes an item.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /* ------------------------------------------------------------------------
     Primary nav keyboard fix.
     Top-level items with a dropdown now render as <button> elements (see
     snippets/header-desktop-nav.liquid). A real <button> already fires a
     'click' event natively for both Enter and Space, so no separate keydown
     handling is needed for activation - only for Escape.
     ------------------------------------------------------------------------ */
  // Shared by the two nav-toggle initialisers below.
  //
  // The accessiBe overlay makes two things unsafe to assume in the nav:
  //
  //  1. That a listener bound to a button stays bound. Its menu module
  //     rewrites the markup it decides is a menu, and a replaced node does
  //     not carry its listeners across - the button is then visibly present
  //     but inert, which is exactly the "toggle does nothing" symptom.
  //     So every handler here is DELEGATED from document and every element
  //     lookup is re-queried at event time. Nothing is captured at init.
  //
  //  2. That Enter/Space on a <button> reaches the button at all. The overlay
  //     binds its own keyboard handling for menus and calls preventDefault,
  //     which suppresses the browser's native activation - so no click event
  //     is ever synthesised and our click handler never runs.
  //     navKeyPassthrough() below restores it.
  //
  // stopPropagation WITHOUT preventDefault is the important detail: it stops
  // the event reaching accessiBe's document-level listener, while leaving the
  // browser's own key->click behaviour intact. Capturing on window (not
  // document) is what makes this reliable - window is the first capture-phase
  // target, so we run before any document-level listener no matter which
  // script loaded first, and the overlay loads asynchronously so load order
  // is not something we can count on.
  function navKeyPassthrough(selector) {
    ['keydown', 'keyup'].forEach(function (type) {
      window.addEventListener(type, function (evt) {
        if (evt.key !== 'Enter' && evt.key !== ' ' && evt.key !== 'Spacebar') return;
        if (!evt.target.closest) return;
        if (!evt.target.closest(selector)) return;
        evt.stopPropagation();
      }, { capture: true });
    });
  }

  function initPrimaryNavDropdowns() {
    var TRIGGER_SELECTOR = '.site-nav--has-dropdown > .a11y-nav-toggle';
    function triggerList() {
      return document.querySelectorAll(TRIGGER_SELECTOR);
    }
    var triggers = triggerList();
    if (!triggers.length) return;

    navKeyPassthrough(TRIGGER_SELECTOR);

    // theme.aio.min.js (compiled bundle, can't be edited) still binds its own
    // touchend handler directly on elements matching .site-nav__link--has-dropdown,
    // assuming a real href to fall back to on a second tap - our button has
    // none, which would break navigation on touch devices at desktop-nav
    // width (iPad landscape, touchscreen laptops). A listener on the button
    // itself would fire in registration order (theirs loaded first, so
    // theirs would still win even with capture:true here) - intercepting on
    // an ancestor during the capture phase runs before the event ever
    // reaches the button, regardless of load order, so this reliably wins.
    // stopPropagation (not preventDefault) only blocks their JS handler;
    // the browser's native touch->click synthesis still proceeds normally,
    // so our own click listener below still receives it.
    document.addEventListener('touchend', function (evt) {
      var trigger = evt.target.closest && evt.target.closest('.site-nav__link--has-dropdown');
      if (trigger && trigger.closest('.site-nav--has-dropdown')) {
        evt.stopPropagation();
      }
    }, { capture: true });

    // Tab reachability is managed here, independent of visibility - see the
    // comment above .a11y-nav-open in accessibility.css for why. Every
    // dropdown link is tabindex="-1" unless the panel was deliberately
    // opened via this button, regardless of whether the theme's own CSS
    // happens to be showing it (hover, or mere Tab-focus on the trigger).
    function setLinksTabbable(li, tabbable) {
      li.querySelectorAll('.site-nav__dropdown a').forEach(function (link) {
        if (tabbable) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    // We drive the theme's OWN visibility class (is-focused) rather than
    // duplicating its show/hide CSS. Previously a11y-nav-open only tracked
    // tabindex while is-focused controlled visibility, so the two could
    // desync - a panel could be visible while its links were untabbable, or
    // a different panel could be showing than the one we thought was open.
    function closeItem(li, btn) {
      li.classList.remove('a11y-nav-open');
      li.classList.remove('is-focused');
      btn.setAttribute('aria-expanded', 'false');
      setLinksTabbable(li, false);
    }

    function openItem(li, btn) {
      li.classList.add('a11y-nav-open');
      li.classList.add('is-focused');
      btn.setAttribute('aria-expanded', 'true');
      setLinksTabbable(li, true);
    }

    // Suppress the theme's open-on-focus. Its accessibleMouseEvent handler is
    // bound to BOTH focusin and mouseover, so simply tabbing onto a trigger
    // pops the panel open. We can't unbind it (compiled bundle), so we undo
    // it on the next frame - after their handler has run. Hover is untouched
    // (:hover check), and a panel we opened deliberately is left alone.
    document.addEventListener('focusin', function (evt) {
      var btn = evt.target.closest && evt.target.closest('.site-nav__link--has-dropdown');
      if (!btn) return;
      var li = btn.closest('.site-nav--has-dropdown');
      if (!li) return;
      requestAnimationFrame(function () {
        if (!li.classList.contains('a11y-nav-open') && !li.matches(':hover')) {
          li.classList.remove('is-focused');
        }
      });
    });

    function closeAll(exceptLi) {
      triggerList().forEach(function (btn) {
        var li = btn.closest('.site-nav--has-dropdown');
        if (li && li !== exceptLi && li.classList.contains('a11y-nav-open')) {
          closeItem(li, btn);
        }
      });
    }

    // Start closed - links excluded from tab order until deliberately
    // opened, regardless of whether the theme's own CSS already shows
    // the panel (e.g. the mouse happens to be resting over it on load).
    triggers.forEach(function (btn) {
      var li = btn.closest('.site-nav--has-dropdown');
      if (li) setLinksTabbable(li, false);
    });

    // Delegated, so it keeps working if the trigger nodes are replaced.
    // Also handles the click-outside-to-close case, since one listener now
    // sees every click on the page.
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) return;
      var btn = evt.target.closest(TRIGGER_SELECTOR);
      if (!btn) {
        if (!evt.target.closest('.site-nav--has-dropdown')) closeAll(null);
        return;
      }
      var li = btn.closest('.site-nav--has-dropdown');
      if (!li) return;

      var wasOpen = li.classList.contains('a11y-nav-open');
      closeAll(li);
      if (wasOpen) {
        closeItem(li, btn);
      } else {
        openItem(li, btn);
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Escape' && evt.keyCode !== 27) return;
      if (!evt.target.closest) return;
      var li = evt.target.closest('.site-nav--has-dropdown');
      if (!li || !li.classList.contains('a11y-nav-open')) return;
      var btn = li.querySelector(':scope > .a11y-nav-toggle');
      if (!btn) return;
      evt.stopPropagation();
      closeItem(li, btn);
      btn.focus();
    });

    // Tabbing focus away from an item's own subtree closes it, so it doesn't
    // stay open after the user has moved on.
    document.addEventListener('focusin', function (evt) {
      triggerList().forEach(function (btn) {
        var li = btn.closest('.site-nav--has-dropdown');
        if (!li || li.contains(evt.target)) return;

        if (li.classList.contains('a11y-nav-open')) {
          closeItem(li, btn);
        }

        // Also clear the theme's own .is-focused. Its compiled bundle adds
        // that class on focusin but only ever removes it on mouseleave -
        // there's no focusout handler in it - so tabbing from the last
        // dropdown trigger straight out of the nav (e.g. to the search box)
        // left the panel visually stuck open. Moving between nav items
        // hides this, because the theme's own accessibleMouseEvent calls
        // closeDropdowns() on the next trigger; only leaving the nav
        // entirely exposes it.
        // The :hover guard matters - .is-focused is the same class the
        // theme uses for mouse hover, so without it, tabbing elsewhere
        // would rip away a menu the user is actively hovering.
        if (li.classList.contains('is-focused') && !li.matches(':hover')) {
          li.classList.remove('is-focused');
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Megamenu column sub-panels (IRIS, DAYLILIES, ...) open on :hover only in
     the theme's CSS, giving them no keyboard path. header-desktop-nav.liquid
     renders a visually-hidden-until-focused toggle button per column; this
     drives it.

     Closed panels are display:none, so their links leave the tab order on
     their own - no tabindex management needed.
     ------------------------------------------------------------------------ */
  function initMegamenuColumnToggles() {
    var COL_SELECTOR = '.dropbtn, .site-nav__deep-dropdown-trigger';
    function toggleList() {
      return document.querySelectorAll('.a11y-subnav-toggle');
    }
    if (!toggleList().length) return;

    // See navKeyPassthrough - same overlay problem, same reason for
    // delegating rather than binding per element.
    navKeyPassthrough('.a11y-subnav-toggle');

    function close(col, btn) {
      col.classList.remove('a11y-subnav-open');
      btn.setAttribute('aria-expanded', 'false');
    }

    function closeAll(except) {
      toggleList().forEach(function (b) {
        var c = b.closest(COL_SELECTOR);
        if (c && c !== except) close(c, b);
      });
    }

    document.addEventListener('click', function (evt) {
      if (!evt.target.closest) return;
      var btn = evt.target.closest('.a11y-subnav-toggle');
      if (!btn) return;
      var col = btn.closest(COL_SELECTOR);
      if (!col) return;

      var isOpen = col.classList.contains('a11y-subnav-open');
      closeAll(col);
      if (isOpen) {
        close(col, btn);
      } else {
        col.classList.add('a11y-subnav-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Escape' && evt.keyCode !== 27) return;
      if (!evt.target.closest) return;
      var col = evt.target.closest(COL_SELECTOR);
      if (!col || !col.classList.contains('a11y-subnav-open')) return;
      var btn = col.querySelector('.a11y-subnav-toggle');
      if (!btn) return;
      evt.stopPropagation();
      close(col, btn);
      btn.focus();
    });

    // Moving focus out of a column closes it, so a panel does not linger
    // open once the user has tabbed past it.
    document.addEventListener('focusin', function (evt) {
      toggleList().forEach(function (btn) {
        var col = btn.closest(COL_SELECTOR);
        if (col && col.classList.contains('a11y-subnav-open') && !col.contains(evt.target)) {
          close(col, btn);
        }
      });
    });
  }


  /* ------------------------------------------------------------------------
     Add-to-cart: keep the shopper on the page.

     theme.js has its own handler that DOES do the right thing - XHR to
     /cart/add.js, set the button text to "Added!", show the cart bubble,
     restore the label after 2s - but it is gated on:

         if (e.target.classList.contains('add-to-cart'))

     The button contains a <span> with the label, so clicking the TEXT makes
     the span the event target, the check fails, nothing is prevented, and
     the form does a normal POST that navigates away to the cart page.
     Pressing Enter while the button itself is focused happens to work.

     We can't edit that compiled handler, so we normalise the event instead:
     when the click starts inside the button but not on the button itself, we
     swallow it and re-dispatch from the button, which the theme's handler
     then accepts. Capture phase so we act before anything else, and the
     synthetic click is what downstream listeners (including our cart
     announcer) see - so nothing double-fires.
     ------------------------------------------------------------------------ */
  function initAddToCartRetarget() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('.add-to-cart');
      if (!btn || btn.disabled) return;
      if (evt.target === btn) return;   // theme's handler will accept this one
      evt.preventDefault();
      evt.stopPropagation();
      btn.click();                      // re-fire with the button as target
    }, { capture: true });
  }


  /* ------------------------------------------------------------------------
     Product gallery thumbnails: stop the theme blurring them on focus.

     theme.js wires each thumbnail's focus event to goToSlide(), and
     goToSlide -> childNavGoTo does:

         this.childNavLinks.forEach(a => { a.blur(); ... })

     So tabbing to a thumbnail immediately blurs it. Focus falls back to
     <body>, which is why the thumbnails never showed a focus ring, why Tab
     appeared to wrap around to the skip link at the top of the page, and
     why the quantity / add-to-cart controls were unreachable by keyboard -
     focus never got past the gallery.

     The bundle is compiled and cannot be edited, and the blur() call is
     unconditional, so we neutralise it per element: assigning an own no-op
     blur shadows the prototype method for these nodes only. Clicking a
     thumbnail still changes slide (that path uses click, not blur), and
     nothing else on the page relies on blurring a gallery thumbnail.
     ------------------------------------------------------------------------ */
  function initGalleryThumbFocusFix() {
    var thumbs = document.querySelectorAll('.product__thumb');
    if (!thumbs.length) return;
    thumbs.forEach(function (thumb) {
      if (thumb.dataset.a11yBlurPatched) return;
      thumb.dataset.a11yBlurPatched = 'true';
      thumb.blur = function () {};
    });
  }

})();
